import React, { useState } from "react";
import { Box, Button, DrawerBody, DrawerFooter, DrawerHeader, Flex, Text } from "@chakra-ui/react";
import { useToast } from "@/components/ui/Toast";
import { FocusDTO, FocusValidationConflictError, createOrUpdateFocus } from "@/js/api/focus";
import { colours } from "@/utils/themeUtils";
import { LibraryDrawerView } from ".";
import { transformSelectedResourcesToMatchAPIContract, transformUpsertResonseToFocusItem } from "./utils";
import { useSelectedClassroomStore } from "@/stores/v2/selectedClassroomStore";
import { useFocusStore } from "@/stores/v2/focusStore";
import { useUserSessionStore } from "@/stores/v2/userSessionStore";
import { EditingFocus } from "./FocusPage";
import { SelectableStudent, StudentCheckboxSelector } from "@/components/StudentCheckboxSelector/StudentCheckboxSelector";
import { ChakraFAIcon } from "@/components/ui/Icon";
import { captureFocusEvent } from "@/utils/posthogUtils";
import { useScreenshareStore } from "@/stores/v2/screenshareStore";

export interface Props {
	closeDrawer: () => void;
	/**
	 * The focus that will be applied to the selected students
	 */
	focusToBeApplied: EditingFocus;
	/**
	 * `classIsActive` is used to determine whether the Apply button should say "Apply Focus" or "Preset Focus" when the `currentScreen` is Classroom.
	 */
	classIsActive?: boolean;
	bubbleAppliedOrUpdatedFocus?: (appliedFocus: FocusDTO) => void;
	jumpToView: (view: LibraryDrawerView) => void;
	handleUpsertFailure: (error: Error) => void;
}

export function SelectStudentsPage(props: Props): JSX.Element {
	const [isApplying, setIsApplying] = useState<boolean>(false);
	const [selectedStudentIds, setSelectedStudentIds] = useState<string[]>([]);

	const userSessionStore = useUserSessionStore();
	const focusStore = useFocusStore();
	const selectedClassroomStore = useSelectedClassroomStore();
	const screenshareStore = useScreenshareStore();

	const { successToast } = useToast();

	const selectableStudents: SelectableStudent[] =
		selectedClassroomStore.students?.map((student) => ({
			value: student.stableIdV1,
			label: `${student.firstName} ${student.lastName}`,
			disabled: props.focusToBeApplied.tabsLocked && screenshareStore.screenshareStudentUsernames.has(student.username),
			disabledReason: "Locked focus unavailable for students in screenshare",
		})) || [];

	function handleApplyFocus(saveToLibrary: boolean): void {
		setIsApplying(true);

		createOrUpdateFocus(
			transformSelectedResourcesToMatchAPIContract(props.focusToBeApplied.resources),
			props.focusToBeApplied.tabsLocked || false,
			true,
			saveToLibrary,
			userSessionStore.getTeacherId() as string,
			props.focusToBeApplied.name,
			selectedClassroomStore.stableIdV1 as string,
			selectedStudentIds,
			props.focusToBeApplied?.id
		)
			.then((response) => {
				successToast({
					title: `Focus has been ${saveToLibrary ? "updated and " : ""}applied to your class.`,
					isClosable: true,
				});

				const focusItem = transformUpsertResonseToFocusItem(response);

				if (!props.focusToBeApplied.isOnTheFly) focusStore.upsertFocusItemInLibrary(focusItem);

				if (props.bubbleAppliedOrUpdatedFocus) props.bubbleAppliedOrUpdatedFocus(focusItem);
				props.closeDrawer();
				//track focus event
				captureFocusEvent(
					!props.classIsActive,
					props.focusToBeApplied.tabsLocked || false,
					userSessionStore.getActiveUser() as string,
					userSessionStore.getActiveDevice() as string
				);
			})
			.catch((e) => {
				console.error("Failed to save focus", e);
				props.handleUpsertFailure(e as Error);

				// Take the teacher back to fix the invalid errors if there are any
				if (e instanceof FocusValidationConflictError) props.jumpToView(LibraryDrawerView.FocusView);
			})
			.finally(() => {
				setIsApplying(false);
			});
	}

	return (
		<>
			<DrawerHeader as={Flex} bg={colours.gray[100]} p={7} justifyContent={"space-between"} alignItems={"center"}>
				<Flex alignItems={"center"}>
					<ChakraFAIcon
						data-testid="go-back-button"
						icon={"fa-arrow-left"}
						variant="solid"
						mr={7}
						boxSize={5}
						cursor={"pointer"}
						onClick={() => props.jumpToView(LibraryDrawerView.FocusView)}
					/>
					<Text data-testid="drawer-heading" fontSize={"xl"} fontWeight={"medium"}>
						Select Students
					</Text>
				</Flex>
			</DrawerHeader>
			<DrawerBody mt={3}>
				<StudentCheckboxSelector students={selectableStudents} handleSelectionChange={setSelectedStudentIds} />
			</DrawerBody>
			<DrawerFooter bg={colours.gray[100]} display={"flex"} justifyContent={"space-between"} alignItems={"center"}>
				<Box></Box>
				<Button
					data-testid="apply-focus-button"
					isDisabled={selectedStudentIds.length < 1}
					onClick={() => handleApplyFocus(!props.focusToBeApplied.isOnTheFly)}
					isLoading={isApplying}
				>
					<ChakraFAIcon icon={"fa-arrows-to-eye"} variant="solid" mr={2} boxSize={4} />
					{props.classIsActive ? "Apply Focus" : "Preset Focus"}
				</Button>
			</DrawerFooter>
		</>
	);
}
